<script setup lang="ts">

  const appConfig = useAppConfig()
  const noCacheFetchOptions = useNoCacheFetchOptions()

  // Currency Store
  const currencyStore = useCurrencyStore()
  const { currency } = storeToRefs(currencyStore)

  //Props
  const props = defineProps({
    hotel_id: {
      type: Number,
      required: true
    },
    link: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    destination: {
      type: String,
      required: true
    },
    price: {
      type: String,
      required: false,
      default: {}
    },
    pack_min_price: {
      type: Object,
      required: false,
      default: {}
    },
    countEstrellas: {
      type: Number,
      required: true
    },
    labelEstrellas: {
      type: [String, null],
      required: true
    },
    pending: {
      type: Boolean,
      required: true
    },
    image: {
      type: String,
      required: true
    },
    hasPrice : {
      type: Boolean,
      required: true
    },
    check_availability: {
      type: Boolean,
      required: true
    }
  })

  // Disponibilidad de habitaciones
  const today = ref(formatDateApi(new Date()))
  const showSkeleton = ref(false)
  const dispo = ref(false)
  if (props.check_availability) {
    showSkeleton.value = true
    const { data: dispoData, status } = await useLazyFetch<HotelDaysAvailabilityResponse>(`${useBaseUrl()}/hotel-days-availability`, {
      key: `dispo-${props.hotel_id}-${today.value}`,
      headers: { 
        "Content-Type": "application/json",
      },
      ...noCacheFetchOptions,
      query:{
        date: today.value,
        hotel_id: props.hotel_id,
        channel_id: appConfig.channel_id,
        currency,
        months: 12
      },
      onRequestError: (error) => {
        logError(error)
      },
    })
    if (status.value === 'success') {
      dispo.value = !objIsEmpty(dispoData.value?.data?.pack_hour)
      showSkeleton.value = false
    }

    watch(dispoData, (newData) => {
      if (newData) {
        dispo.value = !objIsEmpty(newData.data?.pack_hour)
        showSkeleton.value = false
      }
    })
  }

</script>

<template>
  <div class="flex flex-col justify-between h-full">
    <NuxtLink :aria-label="name" :to="link">
      <NuxtImg
        width="400"
        height="200"
        provider="byhours"
        :src="generateImgUrl(image)"
        :alt="name"
        format="webp"
        loading="lazy"
        class="bg-neutral-100 rounded-xl object-cover w-full aspect-16/9 hover:scale-105 transition ease-in-out z-0"
      />
      <div class="mt-4 z-0">
        <span v-if="!countEstrellas" class="text-neutral-500 text-xs">-</span>
        <UIcon v-for="index in countEstrellas" :key="index" name="i-heroicons-star-solid" />
        {{ labelEstrellas }}
      </div>
      <p class="font-bold text-sm sm:text-base">
        {{ name }}
      </p>
      <p class="text-neutral-500 text-xs sm:text-sm mt-1">
        {{ destination }}
      </p>
    </NuxtLink>
    <div v-if="hasPrice">
      <div v-if="!objIsEmpty(pack_min_price)">
        <div class="mt-3 shrink-0">
          <div class="grid grid-rows-1 grid-flow-col grid-cols-3 gap-2">
            <div v-if="pack_min_price?.[3]?.price_label" class="min-w-0">
              <div class="text-xs sm:text-sm text-neutral-500">3 {{ $t('labels.horas') }}</div>
              <div class="text-base sm:text-md font-bold truncate">{{ pack_min_price?.[3]?.price_label }}</div>
            </div>
            <div v-if="pack_min_price?.[6]?.price_label" class="min-w-0">
              <div class="text-xs sm:text-sm text-neutral-500">6 {{ $t('labels.horas') }}</div>
              <div class="text-base sm:text-md font-bold truncate">{{ pack_min_price?.[6]?.price_label }}</div>
            </div>
            <div v-if="pack_min_price?.[24]?.price_label" class="min-w-0">
              <div class="text-xs sm:text-sm text-neutral-500">24 {{ $t('labels.horas') }}</div>
              <div class="text-base sm:text-md font-bold truncate">{{ pack_min_price?.[24]?.price_label }}</div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="price" class="mt-2 inline-block align-baseline">
        <small class="text-neutral-500 text-sm mr-2">{{ $t('buttons.desde').toLowerCase() }}</small>
        <span class="text-xl font-bold">{{ price }}</span>
      </div>
      <div v-else class="mt-3">
        <template v-if="showSkeleton">
          <div class="flex items-center space-x-2">
            <div class="h-6 w-20 bg-gray-100 rounded-full animate-pulse"></div>
          </div>
        </template>
        <template v-else>
          <UBadge v-if="!dispo" color="neutral" variant="subtle" size="md">{{ $t('buttons.noDispo') }}</UBadge>
          <UBadge v-else color="info" variant="subtle" size="md">{{ $t('buttons.dispoOtrosDias') }}</UBadge>
        </template>
      </div>
    </div>
  </div>
</template>